"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";

interface TermsOfServiceProps {
  isYC?: boolean;
}

export default function TermsOfService({ isYC = false }: TermsOfServiceProps) {
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col gap-16 pt-32 pb-16 max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col gap-6 text-center">
          <h1 className="text-h1 text-landing-primary">Terms of Service</h1>
          <p className="text-body text-landing-secondary">Updated on Mar 27, 2024</p>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-8">
          <IntroductionSection />
          <ScopeOfServicesSection />
          <TermsSection isYC={isYC} />
          <ExhibitsSection />
        </div>
      </div>
    </div>
  );
}

function IntroductionSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Introduction</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          Nowadays is an AI-powered event planning copilot that takes the hassle out of organizing corporate events. For
          example, just input event details, and our AI will contact venues and deal with negotiations for you. We are a
          certified travel agency with unlimited network, guaranteeing your team the best bang for your buck. For more
          information about Nowadays, please visit{" "}
          <a href="https://nowadays.ai/" className="text-blue-500 underline">
            https://nowadays.ai/
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function ScopeOfServicesSection() {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-h3 text-landing-primary">Scope of Services</h3>
      <div className="flex flex-col gap-4 text-body text-landing-secondary">
        <p>
          a) Company shall provide sourcing for suitable accommodations, meeting spaces, and/or catering services, as
          well as in making informed decisions regarding the choice of event location.
        </p>

        <div className="flex flex-col gap-4">
          <p>b) The services shall include the following:</p>
          <ul className="list-disc ml-6 flex flex-col gap-2">
            <li>
              For each event, the Company shall provide a shortlist recommendation of five (5) cities and shall deliver
              a minimum of five (5) proposals for each city that fit the criteria, with the aim of facilitating
              Customer's decision-making regarding the event location.
            </li>
            <li>
              Utilizing advantageous benefits of the Company's status as an IATA accredited travel agency to secure
              agency rates, discounts, and negotiated prices for the Customer.
            </li>
            <li>
              Providing Customer with access to local knowledge on the selected city, obtained from Company's
              contractors and Company's relationship with the Convention and Visitors Bureau network. Assisting in
              booking activities and vendors as needed, including but not limited to Audio-Visual (AV) equipment rental,
              and obtaining signed Non-Disclosure Agreements (NDAs) from vendors or venues.
            </li>
            <li>
              The Company agrees to provide Slack support to address live questions or concerns related to the services
              outlined in this agreement.
            </li>
            <li>
              In cases where additional time is required beyond the standard support listed above, the Customer may
              request such time, and the Company can provide it at a rate of $250 per hour based on availability. The
              scheduling of additional time shall be subject to mutual agreement between the parties.
            </li>
            <li>
              How many times can I edit my event details? Your first two changes to event details are free. After that,
              additional changes may incur a fee that will be added to your final invoice.{" "}
              <a
                href="https://docs.google.com/spreadsheets/d/1UHCEskq_SAiveZTOgvKkPg3Na3RYTelb_2P09qs0yQU/edit?gid=0#gid=0"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline"
              >
                View our detailed pricing guide for event changes
              </a>
              .
            </li>
          </ul>
        </div>

        <AuthorizationSection />
      </div>
    </section>
  );
}

function AuthorizationSection() {
  return (
    <div className="flex flex-col gap-4">
      <p className="font-medium">c) Authorization to Sign Contracts:</p>
      <ul className="list-disc ml-6 flex flex-col gap-2">
        <li>
          The Customer hereby authorizes Nowadays to act as its agent and sign contracts on its behalf for the purpose
          of securing venues, services, and vendors related to the event planning process. This authorization includes,
          but is not limited to, venue rental agreements, catering contracts, and vendor service agreements. The
          Customer acknowledges and agrees that they remain fully liable for all financial obligations, terms, and
          conditions of any contracts signed by Nowadays on their behalf. Nowadays will make reasonable efforts to
          obtain the Customer's approval before signing any contracts that exceed pre-agreed budget limits or contain
          unusual terms. The Customer agrees to indemnify and hold Nowadays harmless from any claims, damages, or
          liabilities arising from contracts signed on their behalf, provided Nowadays acted within the scope of this
          authorization.
        </li>
        <li>
          Authority to Bind: By paying a deposit or signing any agreement related to these services, the individual
          acknowledges that they are acting as an authorized agent of the identified organization and that the
          organization is fully bound by these Terms of Service.
        </li>
        <li>
          If the organization (Customer) accepts any benefit from the services (including but not limited to receiving
          sourced venue options, discounted rates, or finalizing bookings), the organization shall be deemed to have
          ratified the individual's authority to bind the organization to the Terms of Service.
        </li>
        <li>
          In the event that the individual is not authorized to bind the organization, such individual will be
          personally responsible for any fees or obligations incurred under these Terms.
        </li>
      </ul>
    </div>
  );
}

function TermsSection({ isYC }: { isYC: boolean }) {
  return (
    <section className="flex flex-col gap-6">
      <h3 className="text-h3 text-landing-primary">Terms</h3>
      <div className="flex flex-col gap-8 text-body text-landing-secondary">
        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">1. Acceptance of Terms</h4>
          <p>
            By accessing or using the services provided by Nowadays, you agree to comply with and be bound by the terms
            and conditions outlined in this agreement.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">2. SAAS Services and Support</h4>
          <div className="flex flex-col gap-2">
            <p>
              Subject to the terms of this agreement, the company will use commercially reasonable efforts to provide
              the customer with the services in accordance with the Service Level Terms attached hereto as Exhibit A.
            </p>
            <p>
              Subject to the terms hereof, the company will provide the customer with reasonable technical support
              services in accordance with the terms set forth in Exhibit B.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">3. Restrictions and Responsibilities</h4>
          <div className="flex flex-col gap-2">
            <p>
              The customer will not, directly or indirectly, reverse engineer, decompile, disassemble, or otherwise
              attempt to discover the source code, object code, or underlying structure, ideas, know-how, or algorithms
              relevant to the services or any software, documentation, or data related to the services ("Software").
              They will not modify, translate, or create derivative works based on the services or any software (except
              to the extent expressly permitted by the company or authorized within the services). The services or any
              software shall not be used for timesharing or service bureau purposes or otherwise for the benefit of a
              third party. Proprietary notices or labels shall not be removed.
            </p>
            <p>
              Furthermore, the customer may not remove or export from the United States or allow the export or re-export
              of the services, software, or anything related thereto, or any direct product thereof in violation of any
              restrictions, laws, or regulations of the United States Department of Commerce, the United States
              Department of Treasury Office of Foreign Assets Control, or any other United States or foreign agency or
              authority.
            </p>
            <p>
              The customer represents, covenants, and warrants that they will use the services only in compliance with
              the company's standard published policies then in effect (the{" "}
              <a href="/privacy-policy" className="text-blue-500 underline">
                Privacy Policy
              </a>
              ) and all applicable laws and regulations. The customer hereby agrees to indemnify and hold harmless the
              company against any damages, losses, liabilities, settlements, and expenses (including without limitation
              costs and attorneys' fees) in connection with any claim or action that directly results from the
              customer's use of services in violation of the Privacy Policy.
            </p>
            <p>
              The customer shall be responsible for obtaining and maintaining any equipment and ancillary services
              needed to connect to, access, or otherwise use the services, including, without limitation, modems,
              hardware, servers, software, operating systems, networking, web servers, and the like (collectively,
              "Equipment"). The customer shall also be responsible for maintaining the security of the Equipment,
              customer account, passwords (including but not limited to administrative and user passwords), and files,
              and for all uses of the customer account or the Equipment with or without the customer's knowledge or
              consent.
            </p>
            <p>
              The customer agrees not to upload any Protected Health Information (PHI) as defined under HIPAA to the
              platform or through the services. The platform is not intended for handling PHI, and the customer must not
              use it for this purpose. The customer shall indemnify the company from any liabilities resulting from the
              breach of this prohibition.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">4. Confidentiality; Proprietary Rights</h4>
          <p>
            Each party (the "Receiving Party") understands that the other party (the "Disclosing Party") has disclosed
            or may disclose business, technical, or financial information relating to the Disclosing Party's business
            (hereinafter referred to as "Proprietary Information" of the Disclosing Party). Proprietary Information of
            the company includes non-public information regarding features, functionality, and performance of the
            Service. Proprietary Information of the customer includes non-public data provided by the customer to the
            company to enable the provision of the services, including any personal data of the customer's employees,
            contractors, or the results of the services provided to the customer ("Customer Data"). The Receiving Party
            agrees: (i) to take reasonable precautions to protect such Proprietary Information and (ii) not to use
            (except in performance of the services or as otherwise permitted herein) or divulge to any third person any
            such Proprietary Information.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">5. Payment of Fees</h4>
          <div className="flex flex-col gap-2">
            <p>
              In consideration for the services rendered by the company, the customer will adhere to the following
              payment terms:
            </p>
            <p>
              1) {isYC ? "$250" : "$500"} Refundable Deposit: To commence event planning, a {isYC ? "$250" : "$500"}{" "}
              refundable deposit is required. This deposit, serving as a commitment to our services, will be applied
              toward the total cost of the event. It is fully refundable if the customer is not satisfied with our
              offerings.
            </p>
            <p>
              2) {isYC ? "For YC affiliated companies, a discounted" : "A"} service fee will be applied to the total
              contract value for any services booked through the company's platform. The service fee is structured as
              follows:
            </p>
            <ul className="list-disc ml-6 flex flex-col gap-1">
              <li>7% for events with total value under $100,000</li>
              <li>6% for events with total value between $100,000 and $500,000</li>
              <li>5% for events with total value between $500,000 and $1,000,000</li>
              <li>4% for events with total value between $1,000,000 and $2,000,000</li>
              <li>3% for events with total value of $2,000,000 or more</li>
            </ul>
            <p>3) A 2.9% processing fee will be applied if payments are handled through Nowadays.</p>
            <p>
              Authority to Bind: By paying a deposit or signing any agreement related to these services, the individual
              acknowledges that they are acting as an authorized agent of the identified organization and that the
              organization is fully bound by these Terms of Service.
            </p>
            <p>
              If the organization (Customer) accepts any benefit from the services (including but not limited to
              receiving sourced venue options, discounted rates, or finalizing bookings), the organization shall be
              deemed to have ratified the individual's authority to bind the organization to the Terms of Service.
            </p>
            <p>
              In the event that the individual is not authorized to bind the organization, such individual will be
              personally responsible for any fees or obligations incurred under these Terms.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">6. Payment Failure and Administrative Fee</h4>
          <p>
            In the event that the payment method provided by the client has an insufficient credit limit or the Stripe
            payment link fails to process the transaction successfully, an additional administrative fee of 1% will be
            charged. This fee is to compensate for the additional time and effort required to address the issue and
            process the payment manually. It is the client's responsibility to ensure that their payment method is
            valid, with sufficient funds or credit, to avoid delays or additional charges.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">7. Term and Termination</h4>
          <div className="flex flex-col gap-2">
            <p>
              Cancellation and Refunds: If the Customer cancels before sourcing starts, all payments are refunded. If
              canceled after sourcing begins but before signing a contract, a $500 fee applies. Once the contract is
              signed, all payments are non-refundable.
            </p>
            <p>
              Subject to earlier termination as provided below, this agreement is for the Initial Service Term as
              specified in the Order Form and will not be automatically renewed for additional periods.
            </p>
            <p>
              Upon any termination, the company will make all Customer Data available to the customer for electronic
              retrieval for a period of thirty (30) days, but thereafter the company may, but is not obligated to,
              delete stored Customer Data. All sections of this agreement which by their nature should survive
              termination will survive termination, including, without limitation, accrued rights to payment,
              confidentiality obligations, warranty disclaimers, and limitations of liability.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">8. Warranty and Disclaimer</h4>
          <p>
            The company shall use reasonable efforts consistent with prevailing industry standards to maintain the
            services in a manner which minimizes errors and interruptions in the services and perform services in a
            professional and workmanlike manner. Services may be temporarily unavailable for scheduled maintenance or
            for unscheduled emergency maintenance, either by the company or by third-party providers, or because of
            other causes beyond the company's reasonable control, but the company shall use reasonable efforts to
            provide advance notice in writing or by e-mail of any scheduled service disruption. However, the company
            does not warrant that the services will be uninterrupted, nor does it make any warranty as to the results
            that may be obtained from use of the services. Except as expressly set forth in this section, the services
            are provided "as is," and the company disclaims all warranties, express or implied, including, but not
            limited to, implied warranties of merchantability and fitness for a particular purpose and non-infringement.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">9. Dispute Resolution</h4>
          <p>
            Any dispute, controversy, or claim arising out of or relating to this agreement or the breach, termination,
            or invalidity thereof shall be settled by binding arbitration in accordance with the rules of the American
            Arbitration Association (AAA). The arbitration shall be conducted by a single arbitrator appointed by mutual
            agreement between the parties or, failing such agreement, by the AAA. The arbitration shall take place in a
            mutually agreed neutral location, and the language of the arbitration shall be English. The arbitrator's
            decision shall be final and binding on the parties and may be enforced in any court of competent
            jurisdiction. To the fullest extent permitted by applicable law, the customer and the company waive the
            right to bring or participate in any class, collective, or representative action or proceeding against the
            other party. Any claims or disputes between the customer and the company will be arbitrated on an individual
            basis and will not be consolidated with any other claims or joined with any other parties' claims.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">10. Indemnity</h4>
          <p>
            The company shall hold the customer harmless from liability to third parties resulting from infringement by
            the service of any United States patent or any copyright or misappropriation of any trade secret, provided
            the company is promptly notified of any and all threats, claims, and proceedings related thereto and given
            reasonable assistance and the opportunity to assume sole control over defense and settlement. The company
            will not be responsible for any settlement it does not approve in writing. The foregoing obligations do not
            apply with respect to portions or components of the service (i) not supplied by the company, (ii) made in
            whole or in part in accordance with customer specifications, (iii) that are modified after delivery by the
            company, (iv) combined with other products, processes, or materials where the alleged infringement relates
            to such combination, (v) where the customer continues allegedly infringing activity after being notified
            thereof or after being informed of modifications that would have avoided the alleged infringement, or (vi)
            where the customer's use of the service is not strictly in accordance with this agreement.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">11. Limitation of Liability</h4>
          <p>
            Notwithstanding anything to the contrary, except for bodily injury of a person, the company and its
            suppliers (including but not limited to all equipment and technology suppliers), officers, affiliates,
            representatives, contractors, and employees shall not be responsible or liable with respect to any subject
            matter of this agreement or terms and conditions related thereto under any contract, negligence, strict
            liability, or other theory: (a) for error or interruption of use or for loss or inaccuracy or corruption of
            data or cost of procurement of substitute goods, services, or technology or loss of business; (b) for any
            indirect, exemplary, incidental, special, or consequential damages; (c) for any matter beyond the company's
            reasonable control; or (d) for any amounts that, together with amounts associated with all other claims,
            exceed the fees paid by the customer to the company for the services under this agreement in the 12 months
            prior to the act that gave rise to the liability, in each case, whether or not the company has been advised
            of the possibility of such damages.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="font-medium text-lg">12. Miscellaneous</h4>
          <p>
            If any provision of this Agreement is found to be unenforceable or invalid, that provision will be limited
            or eliminated to the minimum extent necessary so that this Agreement will otherwise remain in full force and
            effect and enforceable. This Agreement is not assignable, transferable or sublicensable by Customer except
            with Company's prior written consent. Company may transfer and assign any of its rights and obligations
            under this Agreement without consent. This Agreement is the complete and exclusive statement of the mutual
            understanding of the parties and supersedes and cancels all previous written and oral agreements,
            communications and other understandings relating to the subject matter of this Agreement, and that all
            waivers and modifications must be in a writing signed by both parties, except as otherwise provided herein.
            No agency, partnership, joint venture, or employment is created as a result of this Agreement and the
            Customer does not have any authority of any kind to bind the Company in any respect whatsoever. In any
            action or proceeding to enforce rights under this Agreement, the prevailing party will be entitled to
            recover costs and attorneys' fees. All notices under this Agreement will be in writing and will be deemed to
            have been duly given when received, if personally delivered; when receipt is electronically confirmed, if
            transmitted by facsimile or e-mail; the day after it is sent, if sent for next day delivery by recognized
            overnight delivery service; and upon receipt, if sent by certified or registered mail, return receipt
            requested. This Agreement shall be governed by the laws of the State of California without regard to its
            conflict of laws provisions. The provisions for dispute resolution, including binding arbitration and class
            action waiver, are set forth in the "Dispute Resolution" section of this Agreement.
          </p>
        </div>
      </div>
    </section>
  );
}

function ExhibitsSection() {
  return (
    <section className="flex flex-col gap-8">
      <div className="text-center">
        <h3 className="text-h3 underline text-landing-primary">EXHIBIT A</h3>
        <h4 className="text-lg font-medium mt-2 text-landing-primary">Service Level Terms</h4>
      </div>
      <div className="text-body text-landing-secondary">
        <p>
          The Services shall be available 99.9%, measured monthly, excluding holidays and weekends and scheduled
          maintenance. If Customer requests maintenance during these hours, any uptime or downtime calculation will
          exclude periods affected by such maintenance. In the event of Force Majeure events, such as but not limited to
          acts of God, fires, floods, earthquakes, riots, war, acts of terrorism, government actions, or other events
          beyond the reasonable control of the Company that may cause unpredictable and critical downtime, Company shall
          be excused from any delay or failure to perform any obligations under this Agreement. During such events, the
          Service availability and any related downtime credit provisions shall be suspended. Customer's sole and
          exclusive remedy, and Company's entire liability, in connection with Service availability shall be that for
          each period of downtime lasting longer than one hour, Company will credit Customer 5% of Service fees for each
          period of 30 or more consecutive minutes of downtime; provided that no more than one such credit will accrue
          per day. Downtime shall begin to accrue as soon as Customer (with notice to Company) recognizes that downtime
          is taking place, and continues until the availability of the Services is restored. In order to receive
          downtime credit, Customer must notify Company in writing within 24 hours from the time of downtime, and
          failure to provide such notice will forfeit the right to receive downtime credit. Such credits may not be
          redeemed for cash and shall not be cumulative beyond a total of credits for one (1) week of Service Fees in
          any one (1) calendar month in any event. Company will only apply a credit to the month in which the incident
          occurred. Company's blocking of data communications or other Service in accordance with its policies shall not
          be deemed to be a failure of Company to provide adequate service levels under this Agreement.
        </p>
      </div>

      <div className="text-center">
        <h3 className="text-h3 underline text-landing-primary">EXHIBIT B</h3>
        <h4 className="text-lg font-medium mt-2 text-landing-primary">Support Terms</h4>
      </div>
      <div className="text-body landing-text flex flex-col gap-2 text-landing-secondary">
        <p>
          Company will provide Technical Support to Customer via both telephone and electronic mail on weekdays during
          the hours of 9:00 am through 5:00 pm Pacific time, with the exclusion of Federal Holidays ("Support Hours").
        </p>
        <p>Customer may initiate a helpdesk ticket during Support Hours any time by emailing support@nowadays.ai.</p>
        <p>
          Company will use commercially reasonable efforts to respond to all Helpdesk tickets within one (1) business
          day.
        </p>
      </div>

      <div className="text-center">
        <h3 className="text-h3 underline text-landing-primary">EXHIBIT C</h3>
        <h4 className="text-lg font-medium mt-2 text-landing-primary">Testimonial and Reference Account Commitment</h4>
      </div>
      <div className="text-body text-landing-secondary">
        <p>
          The parties shall work together in good faith for the Customer to provide a testimonial and to serve as a
          reference account upon request.
        </p>
      </div>
    </section>
  );
}
